import React from 'react';
import { Table } from 'reactstrap';
import { Server, Vpc } from '~/lib/ApiModel';
import { publicAddresses, ipV4Addresses, privateAddresses } from '~/lib/helper/NetworkHelper';
import { logoForDistribution } from '~/lib/helper/OperatingSystemHelper';
import { networkRoute, serverRoute } from '~/lib/helper/RouteHelper';
import { isCancelledSuspended, isSystemSuspended, canUnCancel } from '~/lib/helper/ServerHelper';
import { formatDate } from '~/lib/helper/StringHelper';
import './ServerTable.scss';

type ServerTableProps = {
    servers: Server[];
    networks?: Vpc[];
    pathname?: string;
};

export default class ServerTable extends React.Component<ServerTableProps> {
    renderServerRow(server: Server) {
        const serverLinkUrl = serverRoute(server) + (this.props.pathname ?? '');
        const networks = this.props.networks ?? [];

        if (isCancelledSuspended(server)) {
            return (
                <tr key={server.id}>
                    <td className="server">
                        <div>
                            <a href={serverLinkUrl}>{server.name}</a>
                        </div>
                        <div>
                            <img
                                src={logoForDistribution(server.image.distribution)}
                                alt={server.image.distribution ?? ''}
                            />
                            {server.image.full_name}
                        </div>
                    </td>
                    <td colSpan={3}>Cancelled and Shutdown</td>
                </tr>
            );
        }

        if (isSystemSuspended(server)) {
            return (
                <tr key={server.id}>
                    <td className="server">
                        <div>
                            <a href={serverLinkUrl}>{server.name}</a>
                        </div>
                        <div>
                            <img
                                src={logoForDistribution(server.image.distribution)}
                                alt={server.image.distribution ?? ''}
                            />
                            {server.image.full_name}
                        </div>
                    </td>
                    <td colSpan={3}>Suspended due to lack of payment</td>
                </tr>
            );
        }

        const publicIps = publicAddresses(ipV4Addresses(server));
        const privateIps = privateAddresses(ipV4Addresses(server));

        const network = server.vpc_id ? networks.find((x) => x.id === server.vpc_id) : null;

        return (
            <tr key={server.id}>
                <td className="server collapsed-header">
                    <div>
                        <a href={serverLinkUrl}>{server.name}</a>
                    </div>
                    {!!server.cancelled_at && (
                        <div>
                            <i>
                                Cancelled, will shutdown{' '}
                                {server.pre_payment?.paid_until
                                    ? `on ${formatDate(server.pre_payment.paid_until)}`
                                    : 'at end of billing period'}
                            </i>
                            {canUnCancel(server) && (
                                <>
                                    {' '}
                                    (<a href={serverRoute(server) + '/cancel-server'}>un-cancel</a>)
                                </>
                            )}
                        </div>
                    )}
                    <div>
                        <img
                            src={logoForDistribution(server.image.distribution)}
                            alt={server.image.distribution ?? ''}
                        />
                        {server.image.full_name}
                    </div>
                </td>
                <td className={publicIps.length === 0 ? 'collapsed-hidden' : undefined}>
                    <span className="th">Public IP</span>
                    <div>
                        {publicIps.map((a) => (
                            <div key={a.ip_address}>{a.ip_address}</div>
                        ))}
                    </div>
                </td>
                <td className={privateIps.length === 0 ? 'collapsed-hidden' : undefined}>
                    <span className="th">Private IP</span>
                    <div>
                        {privateIps.map((a) => (
                            <div key={a.ip_address}>{a.ip_address}</div>
                        ))}
                        {network != null && (
                            <div>
                                <i className="icon ion-md-git-network me-2"></i>
                                <a href={networkRoute(network)}>{network.name}</a>
                            </div>
                        )}
                    </div>
                </td>
                <td>
                    <span className="th">Configuration</span>
                    <div>
                        <div>{server.memory} MB memory</div>
                        <div>{server.disk} GB disk</div>
                    </div>
                </td>
            </tr>
        );
    }

    render() {
        const servers = this.props.servers;
        return (
            <Table responsive className="table-white table-collapse">
                <thead>
                    <tr>
                        <th>Server</th>
                        <th>Public IP</th>
                        <th>Private IP</th>
                        <th>Configuration</th>
                    </tr>
                </thead>

                <tbody>{servers.map((g) => this.renderServerRow(g))}</tbody>
            </Table>
        );
    }
}
